import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import React, { useMemo } from 'react'
import { groupBy, sortBy } from 'lib/array/arrayUtils'
import AccommodationCalendarMonthGrid from './AccommodationCalendarMonthGrid'
import Heading from 'LuxKit/Typography/Heading'

export interface AccommodationMonthView<T = any> {
  /**
   * The year this view is for
   */
  year: number
  /**
   * The month this view is for, indexed from 0.
   */
  month: number
  /**
   * The pricing data for this month
   */
  pricing?: App.Pricing
  /**
   * Whether or not it is sold out
   */
  soldOut?: boolean
  /**
   * The number of items currently being purchased
   * @example Purchasing 2 rooms, the unit count would be 2
   */
  itemCount?: number
  /**
   * The unit of the item being purchased
   * @example Purchasing 2 rooms, the item unit would be room
   */
  itemUnit?: string
  /**
   * How long the item being purchase will goes for
   */
  duration?: number
  /**
   * The unit representing how long it will go for
   */
  durationUnit: 'day' | 'night'
  /**
   * Label that will apear in top right hand corner of month.
   * Ensure only Label components are used
   */
  label?: React.ReactNode
  /**
   * This month is a 'featured' month, for whatever reason.
   * Will apply the flame icon next to the month.
   */
  featured?: boolean
  /**
   * Text that will appear when it's sold out
   * indicating that there is another action the user can take
   * Please handle this extra action manually with the on select
   */
  soldOutExtraActionText?: string
  /**
   * This provides the ability to augment a view with additional data
   * you may want to have carried around to events and other things
   * @default undefined
   */
  additionalData?: T

}

export interface AccommodationYearView {
  year: number
  months: Array<AccommodationMonthView>
}

export type AccommodationCalendarYearSelectHandler = (view: AccommodationMonthView<any>) => void
interface Props {
  views: Array<AccommodationMonthView>
  onSelect?: AccommodationCalendarYearSelectHandler
  className?: string
  priceType?: 'default' | 'member'
}

function AccommodationCalendarMonthPicker(props: Props) {
  const { views, className, onSelect, priceType } = props

  const sortedViews = useMemo<Array<AccommodationYearView>>(() => {
    const yearViews = groupBy(views, view => view.year).entries().map<AccommodationYearView>(([year, views]) => {
      return {
        year,
        months: views,
      }
    }).toArray()
    return sortBy(yearViews, view => view.year, 'asc')
  }, [views])

  return <VerticalSpacer className={className} gap={20}>
    {sortedViews.map(view => <VerticalSpacer gap={12} key={view.year}>
      <Heading variant="heading5">{view.year}</Heading>
      <AccommodationCalendarMonthGrid
        monthViews={view.months}
        onSelect={onSelect}
        priceType={priceType}
      />
    </VerticalSpacer>)}
  </VerticalSpacer>
}

export default AccommodationCalendarMonthPicker
